import { Box, Flex } from '@chakra-ui/react';
import { Facebook, Instagram, Linkedin } from 'lucide-react';
import type { ReactNode } from 'react';
import { FB_URL, INSTAGRAM_URL, LINKEDIN_URL, TIKTOK_URL } from 'utils/constants';
import { BORDER, HEADING, LINK, MUTED } from './footerStyles';

const SOCIAL_ICON_PX = 22;
const SOCIAL_ICON_STROKE = 1.65;

/** Lucide has no TikTok mark — render the official musical-note glyph. */
const TikTokGlyph = () => (
  <Box
    as='svg'
    viewBox='0 0 24 24'
    width={`${SOCIAL_ICON_PX}px`}
    height={`${SOCIAL_ICON_PX}px`}
    fill='currentColor'
    aria-hidden
  >
    <path d='M16.5 3c.3 2.1 1.6 3.7 3.7 4v2.5c-1.3.1-2.6-.3-3.7-1v6.4a5.7 5.7 0 1 1-5.7-5.7c.3 0 .6 0 .9.1v2.6a3.1 3.1 0 1 0 2.2 3V3h2.6Z' />
  </Box>
);

const SOCIALS: { href: string; label: string; icon: ReactNode }[] = [
  {
    href: FB_URL,
    label: 'Facebook',
    icon: <Facebook size={SOCIAL_ICON_PX} strokeWidth={SOCIAL_ICON_STROKE} />,
  },
  {
    href: INSTAGRAM_URL,
    label: 'Instagram',
    icon: <Instagram size={SOCIAL_ICON_PX} strokeWidth={SOCIAL_ICON_STROKE} />,
  },
  {
    href: LINKEDIN_URL,
    label: 'LinkedIn',
    icon: <Linkedin size={SOCIAL_ICON_PX} strokeWidth={SOCIAL_ICON_STROKE} />,
  },
  { href: TIKTOK_URL, label: 'TikTok', icon: <TikTokGlyph /> },
];

interface SocialRowProps {
  /** Chip background. Defaults to the footer's dark hairline. */
  chipBg?: string;
  chipHoverBg?: string;
  /** Optional chip outline. When set, the chip renders a 1px border in this color. */
  chipBorder?: string;
  chipHoverBorder?: string;
  /** Chip diameter. Defaults to 32px. */
  chipSize?: string;
  /** Icon glyph color. Defaults to the footer's muted link color. */
  iconColor?: string;
  iconHoverColor?: string;
}

export const SocialRow = ({
  chipBg = BORDER,
  chipHoverBg = '#3A3531',
  chipBorder,
  chipHoverBorder,
  chipSize = '32px',
  iconColor = LINK,
  iconHoverColor = HEADING,
}: SocialRowProps = {}) => (
  <Flex as='nav' aria-label='Social media' gap={5} align='center' dir='ltr'>
    {SOCIALS.map((s) => (
      <Box
        key={s.label}
        as='a'
        href={s.href}
        target='_blank'
        rel='noopener noreferrer'
        aria-label={s.label}
        display='flex'
        flexDirection='row'
        justifyContent='center'
        alignItems='center'
        p={0}
        w={chipSize}
        h={chipSize}
        bg={chipBg}
        border={chipBorder ? '1px solid' : undefined}
        borderColor={chipBorder}
        borderRadius='9999px'
        color={iconColor}
        _hover={{
          color: iconHoverColor,
          bg: chipHoverBg,
          borderColor: chipHoverBorder ?? chipBorder,
        }}
        _focusVisible={{ outline: '2px solid', outlineColor: MUTED, outlineOffset: '2px' }}
        transition='color 0.15s, background 0.15s, border-color 0.15s'
      >
        {s.icon}
      </Box>
    ))}
  </Flex>
);
